import type * as React from "react";
import { Box, Modal, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useHistory } from "react-router-dom";
import { PATHS } from "constants/pathConstants";
import { COLORS } from "components/v3/Theme/colors";
import GLButton from "components/v3/sharedComponents/GLButton";
import GenomicLifeLegalDisclaimer from "components/v3/sharedComponents/GenomicLifeLegalDisclaimer";
import Mixpanel from "utils/mixpanel";

type CobraDisclaimerModalProps = {
  open: boolean;
  onClose: () => void;
};

const CobraDisclaimerModal: React.FC<CobraDisclaimerModalProps> = ({
  open,
  onClose
}) => {
  const history = useHistory();

  const confirmExtendMembership = () => {
    onClose();
    history.push(PATHS.ACCOUNT_MANAGEMENT_EXPLANATION_OF_BENEFITS);
  };

  const handleNevermindClick = () => {
    Mixpanel.track("Plan information COBRA disclaimer modal Nevermind clicked");
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      aria-labelledby="cobra-disclaimer-title"
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: alpha(COLORS.PRIMARY_DARKEST, 0.52),
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)"
          }
        }
      }}
    >
      <Box
        sx={{
          bgcolor: COLORS.WHITE,
          borderRadius: "18px",
          boxShadow: "0 24px 48px rgba(17, 24, 39, 0.18)",
          boxSizing: "border-box",
          fontFamily: "Inter, sans-serif",
          left: "50%",
          maxHeight: "min(90vh, 720px)",
          maxWidth: "720px",
          outline: 0,
          overflow: "auto",
          position: "absolute",
          top: "50%",
          transform: "translate(-50%, -50%)",
          width: "calc(100% - 32px)",
          p: { xs: 3, sm: 4 }
        }}
      >
        <Box
          sx={{
            alignItems: "flex-start",
            display: "flex",
            gap: 1.5,
            mb: 2.5
          }}
        >
          <Box
            aria-hidden
            component="span"
            sx={{
              flexShrink: 0,
              fontSize: "21px",
              lineHeight: 1,
              mt: "4px"
            }}
          >
            🛑
          </Box>
          <Typography
            id="cobra-disclaimer-title"
            variant="body_strong_lg"
            component="h2"
            sx={{}}
          >
            Please note: This is not a COBRA program
          </Typography>
        </Box>

        <Typography
          variant="body_strong"
          sx={{
            mb: 4
          }}
        >
          Before enrolling here, find out if COBRA may be applicable by
          contacting your previous employer or plan beneficiary.
        </Typography>

        <GenomicLifeLegalDisclaimer
          gap={3}
          onPolicyLinkClick={onClose}
          textSx={{ mb: 0 }}
          typographyVariant="body"
        />

        <Box
          sx={{
            alignItems: { sm: "center", xs: "stretch" },
            display: "flex",
            flexDirection: { sm: "row", xs: "column" },
            gap: 2,
            justifyContent: "flex-start",
            mt: 5
          }}
        >
          <GLButton
            buttonType="primary"
            onClick={confirmExtendMembership}
            text="I understand and agree"
            sx={{
              maxWidth: { sm: "none", xs: "100%" },
              width: { sm: "auto", xs: "100%" }
            }}
          />
          <GLButton
            buttonType="secondary-no-border"
            onClick={handleNevermindClick}
            sx={{
              alignSelf: { sm: "auto", xs: "center" },
              minWidth: "auto",
              px: 2,
              "&:hover:not(.Mui-disabled)": {
                backgroundColor: "transparent",
                borderRadius: "8px !important",
                "& .MuiTypography-root": {
                  color: COLORS.DARK_GRAY
                }
              }
            }}
          >
            <Typography variant="body_strong_lg_dark_gray">
              Nevermind
            </Typography>
          </GLButton>
        </Box>
      </Box>
    </Modal>
  );
};

export default CobraDisclaimerModal;
